/* @flow */

import { getRandom, apiName, apiVersion } from '../utils';
import bidbase from './bidbase';
import { isFetchSupported, fetchJSON } from '../utils/fetch';
import { isJSONSupported, percentageInViewPort } from '../utils/browserutil';
import { logError } from '../utils/logger';
import { extend, buildUrl, shuffle, unique } from '../utils/coreutils';
import { allSettledPromise } from '../utils/promiseutils';
import containerHelper from '../container-helper';
import VisibilityHelper from '../utils/visibilityhelper';
import { getRTBUserEids } from '../idproviders/idputils';
import adconfig from '../adconfig';

const END_POINT = 'https://ssc.33across.com/api/v1/hb';
const SYNC_ENDPOINT = 'https://ssc-cms.33across.com/ps/?m=xch&rt=html&ru=deb';

/**
 * @global
 * @typedef hb_data
 * @property {string} hb_pb Bid value
 * @property {string} hb_bidder Bidder name
 * @property {string} hb_adid Bid ID/creative
 */

/**
 * @global
 * @typedef hbPosData
 * @property {string} info Position Info
 * @property {object} _QP Position Query Param
 * @property {object} _CV Position Custom Vars
 * @property {hb_data} winningBid Winning bid
 */

/**
 * typedef definitions
 */
declare type slotData = {|
  imp: [
    {
      ext: {
        gpid: string,
        ttx: {
          prod: 'siab'
        }
      },
      banner: {
        format: Array<{ w: number, h: number }>
      }
    }
  ]
|};

declare type rtbUid = {|
  id: string,
  atype: number
|};
declare type rtbEid = {|
  source: string,
  uids: Array<rtbUid>
|};
declare type userInfo = {|
  user: {
    ext: {
      eids: Array<rtbEid>
    }
  }
|};
declare type siteInfo = {|
  site: {
    id?: string,
    page: string
  }
|};
declare type regsInfo = {|
  regs: {
    ext: {
      us_privacy: string
    }
  }
|};
declare type extInfo = {|
  ext: {
    ttx: {
      caller: [
        {
          name: string,
          version: string
        }
      ]
    }
  }
|};

declare type a33acrossReqBody = {|
  id: string,
  imp: Array<slotData>,
  ...siteInfo,
  ...userInfo,
  ...regsInfo,
  ...extInfo
|};

declare type a33acrossBidRequestCache = {|
  impid: {
    [string]: string
  }
|};

class _a33across extends bidbase {
  _bidRequest: a33acrossBidRequestCache;

  _config: hb33acrossConfig;

  _DefConfig: hb33acrossConfig;

  constructor(name: string = '33across') {
    super(name);
    this._DefConfig = extend(true, {}, this._DefConfig, {
      qpKey: '33acrosshb',
      bidUrl: END_POINT,
      syncUrl: SYNC_ENDPOINT
    });
    this._isSupported = isJSONSupported() && isFetchSupported();
    this._config = extend(true, {}, this._DefConfig);
    this._mandatoryConfigs = ['bidUrl'];
    this._bidRequest = {
      impid: {}
    };
  }

  // get viewability for the ad position and send it along with
  // impression
  _getViewability(pos: string): number {
    const { cID } = containerHelper.getAdContainerInfo(pos, true);
    if (VisibilityHelper.isDocHidden()) {
      return 0;
    }
    return Math.round(percentageInViewPort(window.top.document, cID));
  }

  _getImpressionForPos(pos: string): slotData {
    // get all the required params
    const sizes = [...this._config.posTokenMap[pos].sizes];

    const format = sizes.map(([w, h]) => ({ w, h }));
    const imp = {
      imp: [
        {
          banner: {
            format,
            ext: {
              ttx: {
                viewability: {
                  amount: this._getViewability(pos)
                }
              }
            }
          },
          ext: {
            gpid: adconfig.getGPID(pos),
            ttx: { prod: 'siab' }
          }
        }
      ]
    };
    return imp;
  }

  _getSiteInfo(): siteInfo {
    return {
      site: {
        page: window.location.href || ''
      }
    };
  }

  _getRegs(): regsInfo {
    return {
      // similar to prebid (openRTB)
      regs: { ext: this._getPrivacyString() }
    };
  }

  _getExt(): extInfo {
    return {
      ext: {
        ttx: {
          caller: [
            {
              name: apiName(),
              version: apiVersion()
            }
          ]
        }
      }
    };
  }

  _buildReqCommonBody(): a33acrossReqBody {
    const eids = getRTBUserEids();

    // build body common for all bid request
    const commonBody = extend(
      true,
      {},
      this._getSiteInfo(),
      this._getRegs(),
      eids,
      this._getExt()
    );
    return commonBody;
  }

  _getBid(pos: string, commonBody: a33acrossReqBody): Promise<boolean> {
    // get impression data for slot/position
    const impression = this._getImpressionForPos(pos);
    const posToken: hb33acrossToken = this._config.posTokenMap[pos];
    // get position specific sizeID
    const { siteId: guid } = posToken;
    // generate unique ID and cache for response lookup later
    const id = getRandom();
    this._bidRequest.impid[id] = pos;
    const reqBody = extend(
      true,
      {},
      commonBody,
      impression,
      { id },
      { site: { id: guid } }
    );
    const opt = {
      data: { guid }, // query params
      timeout: this._config.timeout,
      logPrefix: `${this.getName()} - ${pos}`
    };
    const req = {
      method: 'POST',
      body: JSON.stringify(reqBody),
      credentials: 'include'
    };
    return fetchJSON(this._config.bidUrl, opt, req).then(
      (json: RTBBidResponse) => {
        this._bidResponses[pos] = json;
        return true;
      }
    );
  }

  /**
   * Builds and sends bid requests
   *
   * @param {Array<string>} posList - Ad positions
   * @returns {Promise} - Returns promise
   * @access private
   */
  _getBids(posList: Array<string>): Array<Promise<boolean>> {
    // get common data for all requests
    const commonBody = this._buildReqCommonBody();

    return posList.map(pos => this._getBid(pos, commonBody));
  }

  /**
   * Process bid response. Response format:
   *
   * @param {Array<string>} posList - HB position list
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {object.<string, hbPosData>} - Return data
   */
  _processBidResponse(
    posList: Array<string>,
    ret: hbPartnerData
  ): hbPartnerData {
    posList.forEach(pos => {
      const resp: RTBBidResponse = this._bidResponses[pos];
      if (!this._isValidRTBResponse(resp)) {
        return;
      }

      const {
        seatbid: [
          {
            bid: [res = {}]
          }
        ]
      } = resp;
      const { price: resPrice = 0, adm = '', impid = '', id = '' } = res;
      const price = String(resPrice);
      if (
        price.length > 0 &&
        adm.length > 0 &&
        (impid.length > 0 || id.length > 0)
      ) {
        this._handleBidPosData(ret, pos, price, impid.length ? impid : id, adm);
      }
      delete this._bidResponses[pos];
    });
    return ret;
  }

  /**
   * get bids for given positions
   *
   * @param {Array<string>} posList - HB Position list
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {Promise<object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    this._bidResponses = {};
    this._bidRequest.impid = {};
    // get promise for all bid request
    const pArray = this._getBids(posList);
    return allSettledPromise(pArray).then(
      arr => {
        const fValues = arr
          .filter(v => v.state !== 'fulfilled')
          .map(v => v.reason);
        if (fValues.length) {
          // Log all failed promise errors
          logError(`${this._name} bidding errors: ${fValues.join(',')}`);
        }
        return this._processBidResponse(posList, ret);
      },
      error => {
        logError(`${this._name} bidding failed: ${error || ''}`);
        return ret;
      }
    );
  }

  /**
   * This method will be called for bidders witout overriden userSync method
   *
   * @external syncObj
   * @returns {Array<syncObj>} - Array of sync objects
   * @access private
   */
  _getUserSync(): Array<syncObj> {
    // eslint-disable-next-line camelcase
    const { us_privacy } = this._getPrivacyString();
    // get all unique siteIDs
    // as we have many siteIDs & limit usersync randomize it now
    const siteIDs = shuffle(
      unique(
        Object.values(this._config.posTokenMap).map(
          // $FlowFixMe
          v => v.siteId
        )
      )
    );
    // make sure that syncUrl & pubID is not empty
    if (this._config.syncUrl.length < 1 || siteIDs.length < 1) {
      return [];
    }
    const name = this.getName();
    const type = 'iframe';
    return siteIDs.map(id => {
      // const url = `${syncUrl}&id=${siteId}&gdpr_consent=${encodeURIComponent(consentString)}&us_privacy=${encodeURIComponent(uspConsent)}`
      // eslint-disable-next-line camelcase
      const url = buildUrl(this._config.syncUrl, { id, us_privacy });
      return {
        name,
        type,
        url
      };
    });
  }
}

const a33across: _a33across = new _a33across();
export default a33across;
